<template>
  <div class="day-cal__event-container" ref="dayCalContainer"
       :class="{'day-cal__event-container--no-bg-colors': isCalNoBgColors}">
    <div class="day-cal__event-container__events">
      <div v-for="event in correctEvents"
           :key="event.obj.event_key"
           @click="onEventClick(event, $event)"
           class="day-cal__event"
           :data-cols="event.cols ? event.cols : 1"
           :class="[{'day-cal__event--is-away': event.obj.away}, {'day-cal__event--is-canceled': event.obj.canceled}, {'fc-bgevent__presence': event.obj.event_type === 'presence'}, {'day-cal__event--clickable': isEventsClickable}]"
           :style="{'borderColor': event.obj.color, 'height': event.height + 'px', 'top': (event.top - 1) + 'px', 'left': event.left + '%', 'width': event.width + '%'}">
        <div class="day-cal__event__content" :style="{ fontSize: eventFontSize !== 10 ? eventFontSize + 'px' : '' }">
          <div class="day-cal__event__content__header">
            <div class="d-cal__event-title"><span class="icon-home2 icon-home2-away icon-home2-away--schedules" v-if="event.obj.away"></span>{{ event.obj.title }}</div>
            <div class="d-cal__event-time">
              <span class="d-cal__event-time__start">{{ event.obj.event_starttime }}</span> <span
              class="d-cal__event-time__end">{{ event.obj.event_endtime }}</span>
              <span v-show="event.obj.has_notes && !isCalendarInPrintMode"
                    class="d-cal__event-meta icon-file-text2"></span>
              <span v-show="event.obj.has_substitute" class="d-cal__event-meta icon-users3"></span>
              <div class="d-cal__event-meta d-cal__event-meta__fixed d-cal__event-meta--warning"
                   :class="{'d-cal__event-meta__fixed--canceled': event.obj.canceled}"
                   v-if="event.obj.collision || event.obj.away || event.obj.canceled">
                <span class="icon-notification22" v-if="event.obj.canceled"></span>
                <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                     stroke-width="1.5" stroke="currentColor" class="tw-icon tw-icon--warning">
                  <path stroke-linecap="round" stroke-linejoin="round"
                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"></path>
                </svg>
                {{ getEventWarningLabel(event.obj) }}
              </div>
            </div>
          </div>
          <template v-if="!isHideCalendars">
            <div class="day-cal__event__calendar" v-show="event.obj.teacher"><span class="icon-user"></span>
              {{ event.obj.teacher }}
            </div>
            <div class="day-cal__event__calendar" v-show="event.obj.group"><span class="icon-users"></span>
              {{ event.obj.group }}
            </div>
            <div class="day-cal__event__calendar" v-show="event.obj.room"><span class="icon-location"></span>
              {{ event.obj.room }}
            </div>
            <div class="day-cal__event__calendar" v-show="event.obj.students"><span class="icon-users5"></span>
              {{ event.obj.students }}
            </div>
          </template>
        </div>
        <div class="day-cal__event__bg" :style="{backgroundColor: event.obj.color}"></div>
        <div class="day-cal__event__sider" :style="{backgroundColor: event.obj.color}"></div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import {compareEventsByType} from './eventTypeOrder'

const I18N = {
  da: {
    out_of_school: 'Ude af huset',
    canceled: 'Aflyst'
  },
  en: {
    out_of_school: 'Out of school',
    canceled: 'Canceled'
  },
  nb: {
    out_of_school: 'Utenfor skolen',
    canceled: 'Avlyst'
  }
}

export default {
  props: {
    localeOverride: {
      type: String,
      default: null
    },
    calendarId: {
      default: 7
    },
    events: {
      type: Array,
      default: () => []
    },
    date: {},
    slotHeight: {
      default: 40
    },
    calStartTime: {
      default: '08:00:00'
    },
    calEndTime: {
      default: '17:00:00'
    },
    isEventsClickable: {
      type: Boolean,
      default: false
    },
    isCalendarInPrintMode: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    isCalNoBgColors: {
      type: Boolean,
      default: false
    },
    eventFontSize: {
      default: 10
    },
    isHideCalendars: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      correctEvents: [],
      firstEvent: true,
      isWindowResizing: false
    }
  },
  computed: {
    dayLessons () {
      return this.events.filter(lesson => moment(lesson.start).format('YYYY-MM-DD') === moment(this.date).format('YYYY-MM-DD'))
    },
    locale () {
      if (this.localeOverride) {
        return this.localeOverride
      }
      if (this.$store && this.$store.getters && this.$store.getters.locale) {
        return this.$store.getters.locale
      }
      return 'da'
    },
    i18n () {
      return I18N[this.locale] || I18N.en
    }
  },
  watch: {
    dayLessons () {
      this.updateEventsPositions()
    },
    slotHeight () {
      this.updateEventsPositions()
    },
    calStartTime () {
      this.updateEventsPositions()
    },
    calEndTime () {
      this.updateEventsPositions()
    }
  },
  methods: {
    getEventWarningLabel (eventObj) {
      if (eventObj.canceled) {
        return this.i18n.canceled
      }
      if (eventObj.away) {
        return this.i18n.out_of_school
      }
    },
    updateEventsPositions () {
      this.$nextTick(_ => {
        this.correctEvents = []
        this.formatEvents()
        this.firstEvent = true
      })
    },
    onEventClick (event, evt) {
      if (event.obj.event_type === 'presence') {
        this.$emit('onPresenceClick', event)
      } else {
        evt.stopPropagation()
        this.$emit('onEventClick', event)
      }
    },
    eventTop (event) {
      let calStartTime = moment(event.start).set({
        'hours': moment(this.calStartTime).hours(),
        'minutes': moment(this.calStartTime).minutes()
      })
      let eventStartTime = moment(event.start)
      let duration = moment(eventStartTime).diff(calStartTime)
      let eventTop = ((this.slotHeight * 2) / 60) * moment.duration(duration).asMinutes()
      return eventTop
    },
    eventHeight (event) {
      let duration = moment(event.end).diff(event.start)
      return ((this.slotHeight * 2) / 60) * moment.duration(duration).asMinutes()
    },
    eventBottom (event) {
      let calStartTime = moment(event.start).set({
        'hours': moment(this.calStartTime).hours(),
        'minutes': moment(this.calStartTime).minutes()
      })
      let eventEndTime = moment(event.end)
      let duration = moment(eventEndTime).diff(calStartTime)
      return ((this.slotHeight * 2) / 60) * moment.duration(duration).asMinutes()
    },
    formatEvents () {
      let dayEventsPresence = this.dayLessons.filter(event => event.event_type === 'presence')
      this.setWidthAndLeft(dayEventsPresence)
      let dayEvents = this.dayLessons.filter(event => event.event_type !== 'presence')
      this.setWidthAndLeft(dayEvents)
    },
    setWidthAndLeft (dayLessons) {
      let cluster = []
      let lastEventEnding = null
      let events = dayLessons.map(elem => {
        const top = this.eventTop(elem)
        const height = this.eventHeight(elem)
        const bottom = this.eventBottom(elem)
        return {
          'obj': elem,
          'top': top,
          'height': height,
          'bottom': bottom
        }
      })
      // Group overlapping events into clusters; column order within a cluster is decided by event type
      events = events.sort((e1, e2) => e1.top - e2.top || e2.bottom - e1.bottom)
      for (let i = 0; i < events.length; i++) {
        let e = events[i]
        if (lastEventEnding !== null && e.top >= lastEventEnding) {
          this.packCluster(cluster)
          cluster = []
          lastEventEnding = null
        }
        cluster.push(e)
        if (lastEventEnding === null || e.bottom > lastEventEnding) {
          lastEventEnding = e.bottom
        }
      }
      if (cluster.length > 0) {
        this.packCluster(cluster)
      }
    },
    packCluster (cluster) {
      let columns = []
      cluster.sort(compareEventsByType).forEach(e => {
        const col = columns.find(col => col.every(ev => !this.collidesWith(ev, e)))
        if (col) {
          col.push(e)
        } else {
          columns.push([e])
        }
      })
      columns.forEach(col => col.sort((a, b) => a.top - b.top))
      this.packEvents(columns)
    },
    packEvents (columns) {
      let n = columns.length
      for (let i = 0; i < n; i++) {
        let col = columns[i]
        for (let j = 0; j < col.length; j++) {
          let ev = col[j]
          let colSpan = this.expandEvent(ev, i, columns)
          col[j].left = (i / n) * 100
          col[j].width = 100 * colSpan / n
          col[j].cols = n
          // Added to make absence with events next to it width 80
          if (n > 1 && j === 0) {
            let aTop = col[j].top
            let bTop = columns[j + 1][0].top
            // 20px => it is under the event title
            if (bTop - aTop > 20) {
              col[j].width = 90
            }
          }
          this.correctEvents.push(col[j])
          // Scroll cal to first event
          if (this.firstEvent) {
            this.$emit('lessonsUpdate', this.correctEvents[0])
          }
          this.firstEvent = false
        }
      }
    },
    expandEvent (ev, iColumn, columns) {
      let colSpan = 1
      for (let i = iColumn + 1; i < columns.length; i++) {
        let col = columns[i]
        for (let j = 0; j < col.length; j++) {
          let ev1 = col[j]
          if (this.collidesWith(ev, ev1)) {
            return colSpan
          }
        }
        colSpan++
      }
      return colSpan
    },
    // Check if two events collide
    collidesWith (a, b) {
      return a.bottom > b.top && a.top < b.bottom
    }
  },
  mounted () {
    this.updateEventsPositions()
  }
}
</script>

<style scoped>
.day-cal__event-container,
.day-cal__event-container__events {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
}

.day-cal__event-container__events {
  z-index: 32;
}

.day-cal__event {
  --borderRadius: 3px;
  color: white;
  min-height: 15px;
  display: block;
  font-size: .85em;
  line-height: 1.3;
  font-weight: 400;
  position: absolute;
  z-index: 1;
  top: 20px;
  left: 0%;
  right: 0%;
  border-right: 1px solid transparent !important;
  border-top: 1px solid transparent !important;
  border-radius: var(--borderRadius);
}

.day-cal__event.day-cal__event--clickable:not(.fc-bgevent__presence) {
  cursor: pointer;
}

.day-cal__event-container--no-bg-colors .day-cal__event__content {
  color: #222;
  border: 1px solid;
  text-shadow: none;
  background: #fff;
}

.day-cal__event-container--no-bg-colors .day-cal__event__bg {
  display: none;
}


.day-cal__event__content {
  color: white;
  position: absolute;
  width: 100%;
  height: 100%;
  padding: 1px 3px 1px 8px;
  left: 0;
  top: 0;
  font-size: 10px;
  z-index: 1;
  overflow: hidden;
  border-radius: var(--borderRadius);
  text-shadow: 0px 1px 0px rgb(140 140 140 / 40%);
}

.day-cal__event__bg {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  opacity: 0.8;
  border-radius: var(--borderRadius);
}

.day-cal__event__sider {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 4px;
  z-index: 2;
  border-radius: var(--borderRadius);
}

.d-cal__event-meta {
  margin-left: 2px;
}

.fc-bgevent__presence {
  background: rgba(153, 197, 244, 0.3) !important;
  border-radius: 0 !important;
  border: none !important;
}

.fc-bgevent__presence .day-cal__event__content {
  display: none;
}

.d-cal__event-title {
  font-weight: bold;
  overflow: hidden;
  white-space: nowrap;
}

.day-cal__event__content__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
}

.day-cal__event__calendar {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: left;
}

.d-cal__event-time__start {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.d-cal__event-time__start:after {
  content: ' - ';
}

.day-cal__event:not([data-cols="1"]):not([data-cols="2"]) .day-cal__event__content {
  font-size: 8px;
}

.day-cal__event:not([data-cols="1"]) .day-cal__event__content {
  font-size: 9px;
}

.day-cal__event:not(.day-cal__event--dragged):not(.fc-bgevent__presence):not([data-cols="1"]):hover .d-cal__event-time {
  display: block;
}

.day-cal__event--is-canceled .day-cal__event__content {
  color: #71717a;
  text-shadow: none;
}

.day-cal__event--is-canceled .day-cal__event__bg {
  position: relative;
  background-color: #fff !important;
  background: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' preserveAspectRatio='none' viewBox='0 0 100 100'><path d='M100 0 L0 100 ' stroke='red' stroke-width='2'/><path d='M0 0 L100 100 ' stroke='red' stroke-width='2'/></svg>");
  background-repeat: no-repeat;
  background-position: center center;
  background-size: 100% 100%, auto;
}

.d-cal__event-meta__fixed.d-cal__event-meta__fixed--canceled {
  background-color: #fddddd;
  color: #c20006;
  padding: 2px 4px;
}
.d-cal__event-meta__fixed {
  position: absolute;
  top: 0;
  right: 0;
  font-weight: normal;
  border-radius: 0 0 0 4px;
  font-size: 10px;
  line-height: 0;
  padding: 1px;
  color: #a26206;
  background-color: #fefce8;
  display: flex;
  align-items: center;
  gap: 0.25rem
}

.d-cal__event-meta .tw-icon {
  width: 13px;
}

.tw-icon--warning {
  margin-bottom: -1px;
}
</style>



// WEBPACK FOOTER //
// DCalendarDay.vue?1aa73251